/**
 * Navbar — top-level navigation shell. The heavy bits live in
 * sibling sub-components:
 *   - `nav/navbarTopNavRender.jsx`   → 5 flagship link renderers
 *   - `nav/NavGroupDropdown.jsx`     → grouped dropdown (Learn / Watch / Tools)
 *   - `nav/NavbarUserMenu.jsx`       → right-side avatar + dropdown
 *   - `nav/NavbarMobileDrawer.jsx`   → ≤768px hamburger drawer
 *   - `nav/NavGroupingTour.jsx`      → first-time visitor tour
 * This file is intentionally a thin orchestration layer.
 */
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Menu, X, HelpCircle } from "lucide-react";
import useTheme from "@/lib/useTheme";
import useProPricing from "@/lib/useProPricing";
import CurrencyPill from "@/components/CurrencyPill";
import SearchCommand from "@/components/SearchCommand";
import {
  SortableNav, NavCustomiseToggle, HiddenNavDrawer, useNavOrder,
} from "@/components/nav/SortableNav";
import NavGroupDropdown from "@/components/nav/NavGroupDropdown";
import NavGroupingTour from "@/components/nav/NavGroupingTour";
import NavbarUserMenu from "@/components/nav/NavbarUserMenu";
import NavbarMobileDrawer from "@/components/nav/NavbarMobileDrawer";
import { NAV_GROUPS } from "@/components/nav/navGroups";
import { renderTopNavItem } from "@/components/nav/navbarTopNavRender";

const linkClass = ({ isActive }) =>
  `text-sm transition-colors whitespace-nowrap ${
    isActive ? "text-rp-text" : "text-rp-textDim hover:text-rp-text"
  }`;

const helpLinkClass = ({ isActive }) =>
  `grid h-8 w-8 place-items-center rounded-full border transition-colors ${
    isActive
      ? "border-rp-borderHover bg-rp-surface2 text-rp-text"
      : "border-rp-border text-rp-textDim hover:text-rp-text hover:border-rp-borderHover"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(false);
  const pricing = useProPricing();
  const { order, hidden, saveOrder, hideItem, showItem, resetOrder, loaded } = useNavOrder();

  const closeMobile = () => setMobileOpen(false);
  const renderItem = (id, cls = linkClass) =>
    renderTopNavItem(id, cls, pricing, closeMobile);

  const inlineTopNav = () => {
    if (user && loaded) {
      return (
        <SortableNav
          items={order}
          hidden={hidden}
          editing={editingOrder}
          onReorder={saveOrder}
          onHide={hideItem}
          renderItem={renderItem}
        />
      );
    }
    const hiddenSet = new Set(hidden);
    return order
      .filter((id) => !hiddenSet.has(id))
      .map((id) => <span key={id}>{renderItem(id)}</span>);
  };

  return (
    <header
      className="rp-glass rp-safe-top sticky top-0 z-50 border-b border-rp-border"
      data-testid="navbar"
    >
      <NavGroupingTour />
      <div className="rp-container flex min-h-16 flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <span className="h-2 w-2 rounded-full bg-rp-accent shadow-[0_0_12px_#4F7CFF]" />
          <span className="font-display text-lg font-extrabold tracking-tight text-rp-text">
            RiskPedia
          </span>
        </Link>

        {/* Tablet+ inline nav: 5 flagship items + 3 grouped dropdowns + Help icon */}
        <nav
          className="hidden md:flex flex-1 flex-wrap items-center justify-center gap-x-5 gap-y-1 lg:gap-x-6 min-w-0"
          data-testid="nav-inline"
        >
          {inlineTopNav()}
          {NAV_GROUPS.map((g) => (
            <NavGroupDropdown
              key={g.id}
              label={g.label}
              testId={g.testId}
              items={g.items}
              onItemClick={closeMobile}
            />
          ))}
          {user && (
            <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard" onClick={closeMobile}>
              Dashboard
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin" onClick={closeMobile}>
              Admin
            </NavLink>
          )}
          <NavLink
            to="/help"
            className={helpLinkClass}
            title="Help"
            data-testid="nav-help"
            onClick={closeMobile}
          >
            <HelpCircle className="h-4 w-4" />
          </NavLink>
          {user && (
            <NavCustomiseToggle
              editing={editingOrder}
              onToggle={setEditingOrder}
              onReset={resetOrder}
              hasOrder
            />
          )}
          {user && editingOrder && (
            <HiddenNavDrawer hidden={hidden} onShow={showItem} />
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <SearchCommand />
          <CurrencyPill />
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            data-testid="theme-toggle-btn"
            className="grid h-9 w-9 place-items-center rounded-full border border-rp-border bg-rp-surface text-rp-textDim hover:text-rp-text hover:border-rp-borderHover transition-colors"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
          {!user ? (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden md:block">
                <Button variant="ghost" className="text-rp-textDim hover:text-rp-text hover:bg-rp-surface2">
                  Log in
                </Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup" className="hidden md:block">
                <Button className="bg-rp-accent hover:bg-rp-accentHover text-white font-body font-medium">
                  Get started
                </Button>
              </Link>
            </>
          ) : (
            <NavbarUserMenu user={user} onLogout={logout} />
          )}

          {/* Mobile-only hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="md:hidden grid h-9 w-9 place-items-center rounded-full border border-rp-border bg-rp-surface text-rp-textDim hover:text-rp-text"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <NavbarMobileDrawer
          user={user}
          order={order}
          hidden={hidden}
          renderTopNavItem={(id, cls) => renderTopNavItem(id, cls, pricing, closeMobile)}
          onClose={closeMobile}
        />
      )}
    </header>
  );
}
